import { Alert, Col, Row, Space, Switch } from 'antd';
import {
  FloorplanGeoPersonalizationPanel,
  PersonalizationSteps,
  usePersonalizationSteps,
} from '../Personalization.Details';
import { PersonalizationDetails } from './PersonalizationDetails';
import { FinalizationAlert } from './FinalizationAlert';
import { FinalizationResidents } from './FinalizationResidents';
import { FinalizeButton } from './FinalizeButton';
import { PaymentsPanel } from './Payments';
import { useCommunityContext, useProjectContext } from '@/views';
import { useGetProjectHomeInfoQuery } from '@/redux';
import { PersonalizationSummaryTotalBalance } from './PersonalizationSummaryTotalBalance';
import {
  PriceColumnsCheckboxGroup,
  usePriceColumnsCheckboxGroup,
} from './PriceColumnsCheckboxGroup';
import { useTranslation } from 'react-i18next';
import { isArray } from 'lodash';
import { openFileInNewWindow, useBool } from '@/utils';
import { DownloadPersonalizationSummaryPdfButton } from './Pdf/DownloadPersonalizationSummaryPdfButton';
import { FilePdfOutlined } from '@ant-design/icons';
import { useCallback } from 'react';
import {
  PersonalizationSummaryFormValue,
  PERSONALIZATION_SUMMARY_FORM_INITIAL_VALUE,
} from './Pdf/DownloadPersonalizationSummaryPdfModal';
import { DndProviderControlProps, spinner } from '@/components';
import {
  CommunityPermissions,
  personalizationsHttp,
  ProjectStepLookupDto,
} from '@/core';

interface Props extends DndProviderControlProps {
  homeId: string;
  communityId: string;
  projectId: string;
  isPublic?: boolean;
  floorplanImageInnerRef?: React.Ref<HTMLDivElement>;
}

export function useDownloadPdf(
  projectId: string,
  homeId: string,
  stepId?: string,
) {
  return useCallback(
    async (value: PersonalizationSummaryFormValue) => {
      if (value.size === 'Tabloid') {
        return await personalizationsHttp.downloadTabloidPdf({
          ...value,
          projectId,
          homeId,
          stepId,
        });
      }
      return await personalizationsHttp.downloadPdf({
        ...value,
        projectId,
        homeId,
        stepId,
      });
    },
    [projectId, homeId, stepId],
  );
}

const useDownload = ({
  projectId,
  homeId,
  selectedStepId,
}: {
  projectId: string;
  homeId: string;
  selectedStepId?: string;
}) => {
  const downloadPdf = useDownloadPdf(projectId, homeId, selectedStepId);
  return useCallback(
    (values: PersonalizationSummaryFormValue, onClose: () => void) => {
      spinner.show();
      downloadPdf(values)
        .then((file) => {
          openFileInNewWindow(file);
        })
        .finally(() => {
          spinner.hide();
          onClose();
        });
    },
    [downloadPdf],
  );
};

export function PersonalizationSummaryContent(props: Props) {
  const {
    homeId,
    projectId,
    communityId,
    isPublic,
    floorplanImageInnerRef,
    skipProvider,
  } = props;

  const { t } = useTranslation();

  const { isActive } = useProjectContext();

  var { data: projectHome } = useGetProjectHomeInfoQuery({
    id: projectId,
    homeId,
    allowAnonymous: isPublic,
  });

  const steps = usePersonalizationSteps({ projectId, projectHome, isPublic });
  const priceColumnsCheckboxGroup = usePriceColumnsCheckboxGroup();
  const { showBaseCost, showResidentPrice, showOwnerPrice } =
    priceColumnsCheckboxGroup;
  const selectedStepId = isArray(steps.selected)
    ? steps.selected[0]
    : steps.selected;

  const selectedStep = steps.steps.find((x) => x.id === selectedStepId);

  const allPersonalizationsSelected = !selectedStep;

  const [showFloorplanImage, , toggleShowFloorplanImage] = useBool(true);
  const [showInactive, , toggleShowInactive] = useBool(false);

  const onDownLoad = useDownload({
    homeId,
    projectId,
    selectedStepId: selectedStep?.id,
  });

  if (!projectHome?.menuId) {
    return (
      <Alert
        type="info"
        description={t('personalization.floorplan.geo.noMenu')}
      />
    );
  }

  return (
    <Space direction="vertical" size="middle" className="w-100">
      {!isPublic && (
        <PersonalizationSummaryHeader
          steps={steps}
          onDownLoad={onDownLoad}
          communityId={communityId}
          projectId={projectId}
          homeId={homeId}
          selectedStep={selectedStep}
        />
      )}

      {!isPublic && selectedStep?.id && isActive && (
        <FinalizeButton
          projectId={projectId}
          homeId={homeId}
          selectedStepId={selectedStep?.id}
        />
      )}
      <Row justify={isPublic ? 'end' : 'start'} className="w-100">
        <Space className="mb-0">
          <Switch
            checked={showFloorplanImage}
            onChange={toggleShowFloorplanImage}
          />
          {t('personalization.summary.showFloorplanImage')}
        </Space>
        {isPublic && (
          <Space className="mb-0 ms-2">
            <Switch checked={showInactive} onChange={toggleShowInactive} />
            {t('personalization.summary.showInactive')}
          </Space>
        )}
      </Row>

      {!isPublic && allPersonalizationsSelected && (
        <Col span={12}>
          <FinalizationAlert homeId={homeId} projectId={projectId} />
        </Col>
      )}
      {showFloorplanImage && (
        <FloorplanGeoPersonalizationPanel
          homeId={homeId}
          projectId={projectId}
          selectedStepIds={selectedStep ? [selectedStep.id] : undefined}
          readonly
          isPublic={isPublic}
          innerRef={floorplanImageInnerRef}
          skipProvider={skipProvider}
        />
      )}
      {!isPublic && (
        <PriceColumnsCheckboxGroup {...priceColumnsCheckboxGroup} />
      )}

      <PersonalizationDetails
        homeId={homeId}
        projectId={projectId}
        stepId={selectedStep?.id}
        showBaseCost={showBaseCost}
        showResidentPrice={showResidentPrice}
        showOwnerPrice={showOwnerPrice}
        className="mb-3"
        isPublic={isPublic}
        communityId={communityId}
        showInactive={isPublic ? showInactive : true}
      />
      {!isPublic && (
        <PaymentsPanel
          className="w-100"
          homeId={homeId}
          projectId={projectId}
          stepId={selectedStep?.id}
        />
      )}

      {!isPublic && allPersonalizationsSelected && (
        <PersonalizationSummaryTotalBalance
          homeId={homeId}
          projectId={projectId}
          showBaseCost={showBaseCost}
          showResidentPrice={showResidentPrice}
        />
      )}
      {!isPublic && projectHome?.isActive && (
        <FinalizationResidents
          projectId={projectId}
          homeId={homeId}
          selectedStepId={selectedStep?.id}
        />
      )}
    </Space>
  );
}

const PersonalizationSummaryHeader = (props: {
  steps: ReturnType<typeof usePersonalizationSteps>;
  onDownLoad: (
    values: PersonalizationSummaryFormValue,
    onClose: () => void,
  ) => void;
  communityId: string;
  projectId: string;
  homeId: string;
  selectedStep?: ProjectStepLookupDto;
}) => {
  const { steps, onDownLoad, communityId, projectId, homeId, selectedStep } =
    props;
  const { t } = useTranslation();
  const { hasPermission } = useCommunityContext();

  return (
    <Space direction="horizontal" wrap>
      <PersonalizationSteps {...steps} />
      <DownloadPersonalizationSummaryPdfButton
        title={t('downloadPdf')}
        openButtonTitle={t('downloadPdf')}
        openButtonIcon={<FilePdfOutlined />}
        downloadButtonTitle={t('download')}
        downloadButtonIcon={<FilePdfOutlined />}
        layout="one-column"
        onDownload={onDownLoad}
        filterBy={[
          'category',
          ...(hasPermission(CommunityPermissions.BaseCost.FullAccess)
            ? (['baseCost'] as const)
            : []),
        ]}
        initialValue={{
          ...PERSONALIZATION_SUMMARY_FORM_INITIAL_VALUE,
          communityIds: [communityId],
          projectIds: [projectId],
          homeIds: [homeId],
          stepIds: selectedStep ? [selectedStep.id] : null,
        }}
      />
    </Space>
  );
};
